import * as Clipboard from 'expo-clipboard';
import { useCallback, useRef, useState, useEffect } from 'react';
import {
  FlatList,
  Image,
  LayoutAnimation,
  ListRenderItemInfo,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { StatusToast } from '@components/CustomToast/StatusToast.web';
import Highlighter from '@components/HighlightWords';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { Box, Text } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { MemberRowSkeleton, SkeletonList } from '@components/shared/skeletons';
import { AddMembersInput } from '@components/TaskList/AnimatedSearchBox.web';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import {
  Group,
  TeamMemberRole,
  User,
  useCreateTeamInvitationMutation,
  useCreateTeamMemberInvitationMutation,
  GetTeamDetailDocument,
  ListPendingUserRequestsDocument,
  Team,
  UserRequestCategory,
  UserRequestable,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import { useContacts } from '@hooks/useContacts';
import useMe from '@hooks/useMe';
import { useMemberSearch } from '@hooks/useMemberSearch';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import theme from '@themes/theme';
import { logEvent } from '@utils/eventLogger';
import { TEAM_ROLE_DESCRIPTIONS } from '@utils/teams';

import {
  buildEmailSuggestion,
  dedupeUsersByIdAndEmail,
  filterTasktagUsers,
} from './inviteMemberSearch';

export type DeferredInvitation = { email: string; role: TeamMemberRole };

type InviteMemberModalProps = {
  onClose: () => void;
  // Dismiss without completing (X button / backdrop). Several call sites only
  // supply onClose, so this falls back to it — a missing onCancel used to
  // throw on X-click, leaving the modal impossible to dismiss.
  onCancel?: () => void;
  isVisible: boolean;
  teamId: string;
  onMembersAdded?: (members?: DeferredInvitation[]) => void;
  requireMemberSelection?: boolean; // If false, allows sending invite without selecting members
  onDeferredSend?: (members: DeferredInvitation[]) => void; // When supplied, Send Invite returns members to caller instead of calling the mutation
};

export type UserSearchType = Pick<
  User,
  'id' | 'name' | 'firstName' | 'lastName' | 'email' | 'avatar' | '__typename'
> & {
  role: TeamMemberRole;
};

type StringSearchType = {
  title: string;
  role: TeamMemberRole;
  __typename: string;
};

// Selected group type used in the assign panel
type SelectedGroupType = Group & {
  users?: UserSearchType[];
};

// RoleOptionItem component for individual role option
const RoleOptionItem = ({
  title,
  description,
  id,
  onPress,
}: {
  title: string;
  description?: string;
  id: TeamMemberRole;
  onPress: (roleId: TeamMemberRole) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TouchableOpacity
      onPress={() => onPress(id)}
      // @ts-ignore - web-specific props
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <Box
        px='xs'
        py='xs'
        flex={1}
        flexDirection='column'
        backgroundColor={isHovered ? 'grey01' : 'white'}>
        <Text
          ml='xs'
          accessibilityLabel={title}
          variant='webBodySecondary'
          color='textPrimary'
          numberOfLines={1}>
          {title}
        </Text>
        {!!description && (
          <Text
            ml='xs'
            marginTop='xxxs'
            variant='webBodyHintSubline'
            color='grey04'
            numberOfLines={2}>
            {description}
          </Text>
        )}
      </Box>
    </TouchableOpacity>
  );
};

// TeamMemberRoleSelector component for dropdown
const TeamMemberRoleSelector = ({
  onRoleSelect,
}: {
  onRoleSelect: (role: TeamMemberRole) => void;
}) => {
  const roleOptions = [
    {
      title: 'Admin',
      description: TEAM_ROLE_DESCRIPTIONS[TeamMemberRole.Admin],
      id: TeamMemberRole.Admin,
    },
    {
      title: 'Member',
      description: TEAM_ROLE_DESCRIPTIONS[TeamMemberRole.Member],
      id: TeamMemberRole.Member,
    },
  ];

  const handleRolePress = (roleId: TeamMemberRole) => {
    onRoleSelect?.(roleId);
  };

  return (
    <Box
      width={320}
      py='xxs'
      style={{
        borderRadius: theme.borderRadii.xs,
        borderWidth: 1,
        borderColor: theme.colors.grey02,
      }}>
      {roleOptions.map(({ title, description, id }) => (
        <RoleOptionItem
          key={id}
          title={title}
          description={description}
          id={id}
          onPress={handleRolePress}
        />
      ))}
    </Box>
  );
};

export const InviteMemberModal = (props: InviteMemberModalProps) => {
  const {
    onCancel,
    onClose,
    isVisible,
    teamId,
    onMembersAdded,
    requireMemberSelection = true,
    onDeferredSend,
  } = props;
  // X button / backdrop dismissal — fall back to onClose when the call site
  // doesn't distinguish cancel from close.
  const handleCancel = onCancel ?? onClose;
  const { client } = useApolloClient();
  const { selectedTeam: team, setSelectedTeam } = useWebDrawerNavigator();
  const toast = useToast();
  const { me } = useMe();

  const { contacts, loading: contactsLoading } = useContacts();

  const {
    searchVal,
    setText,
    focusOnSearch,
    setFocusOnSearch,
    shouldShowDropdown,
    listGroup,
    listContacts,
    listTasktagUsers,
    expandedGroupIds,
    searchExpandedGroupIds,
    toggleGroupExpanded,
    toggleSearchGroupExpanded,
    resetExpanded,
    isSearchLoading,
  } = useMemberSearch({
    contacts,
    meId: undefined,
    includeContacts: false,
    useLayoutAnimation: true,
  });

  // Selected standalone users (not belonging to a selected group)
  const [selectedUserList, setSelectedUserList] = useState<UserSearchType[]>(
    []
  );

  // Selected email suggestion items (if needed)
  const [selectedEmailList, setSelectedEmailList] = useState<
    StringSearchType[]
  >([]);
  // Selected groups (UI only, group header + nested users with roles)
  const [selectedGroupList, setSelectedGroupList] = useState<
    SelectedGroupType[]
  >([]);

  const inputRef = useRef<TextInput>(null);

  // Reset all selection state when the modal closes
  useEffect(() => {
    if (!isVisible) {
      setSelectedUserList([]);
      setSelectedEmailList([]);
      setSelectedGroupList([]);
      setText('');
      resetExpanded();
    }
  }, [isVisible]);

  // Helper function to check if a user is already in the team
  const isUserAlreadyInTeam = useCallback(
    (userId: string): boolean => {
      if (!team?.users) return false;
      return team.users.some((u) => u.id === userId);
    },
    [team?.users]
  );

  // Build a display name for a User
  const getDisplayName = (t: User) => {
    const { name, firstName, lastName } = t;
    return name ?? `${firstName ?? ''} ${lastName ?? ''}`.trim();
  };

  // Contact rows are always deduped by id and by normalized email (TA-10115:
  // duplicate contact records from different sources collapse to one row).
  // While searching, global search results are merged in first.
  const mergedContacts = dedupeUsersByIdAndEmail(
    searchVal.trim().length > 0 ? [...contacts, ...listContacts] : contacts
  );

  const filteredList = mergedContacts
    .filter((t: User) => {
      if (searchVal.length === 0) return true;
      if (selectedUserList.some((t1) => t.id === t1.id)) return false;

      const displayName = getDisplayName(t);
      const email = t.email || '';
      const searchLower = searchVal.toLowerCase();

      return (
        displayName.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower)
      );
    })
    .map((t) => {
      return { ...t, role: TeamMemberRole.Member } as UserSearchType;
    });

  // TA-10115: "More on TaskTag" must exclude contacts (they already show as
  // contact rows), selected users, and the current user — matching the
  // project invite modal's _filteredTasktagUsers shaping.
  const filteredTasktagUsers = dedupeUsersByIdAndEmail(
    filterTasktagUsers({
      tasktagUsers: listTasktagUsers,
      contacts: mergedContacts,
      selectedUserIds: new Set(selectedUserList.map((u) => u.id)),
      meId: me?.id,
    })
  );

  // TA-10115: only suggest "Invite: <email>" when the email is not already a
  // contact, registered user, group member, team member, or selected email.
  const suggestionEmail = buildEmailSuggestion({
    searchVal,
    contacts: mergedContacts,
    tasktagUsers: listTasktagUsers,
    groups: listGroup,
    teamUsers: team?.users ?? [],
    selectedEmails: selectedEmailList.map((e) => e.title),
  });
  const suggestionList: StringSearchType[] = suggestionEmail
    ? [
        {
          title: suggestionEmail,
          role: TeamMemberRole.Member,
          __typename: 'StringSearchType',
        },
      ]
    : [];

  // Users that belong to selected groups (for UI filtering only)
  const groupUserIds = new Set(
    selectedGroupList.flatMap((g) => g.users?.map((u) => u.id) ?? [])
  );

  // Only show users that are not part of any selected group as standalone rows
  const visibleSelectedUsers = selectedUserList.filter(
    (u) => !groupUserIds.has(u.id)
  );

  const [copied, setCopied] = useState(false);

  const COPY_LINK_TOAST_KEY = 'copy_link_toast_shown';

  const copyToClipboard = useCallback(
    async (link: string) => {
      if (!link) return;
      await Clipboard.setStringAsync(link);

      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 3000);

      const alreadyShown = localStorage.getItem(COPY_LINK_TOAST_KEY);
      if (!alreadyShown) {
        localStorage.setItem(COPY_LINK_TOAST_KEY, '1');
        toast.show(
          <TaskCreatedToast
            title='Link copied'
            message='Paste it to any messaging app to invite your team.'
            duration={3000}
            minWidth={380}
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              marginBottom: 39,
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
          }
        );
      }
    },
    [toast]
  );

  const [
    createTeamInvitation,
    { loading: copyLinkLoading, data: createTeamInvitationData },
  ] = useCreateTeamInvitationMutation();

  useEffect(() => {
    const raw = createTeamInvitationData?.createTeamInvitation;
    if (!raw) return;
    const tokenLink =
      raw.replace('register/register-with-email', 'invited-user') +
      '&type=team';
    copyToClipboard(tokenLink);
  }, [createTeamInvitationData]);

  const assignDisable =
    selectedUserList.length === 0 &&
    selectedEmailList.length === 0 &&
    selectedGroupList.length === 0;

  // Add a group into selectedGroupList
  // Requirement: when selecting a group, remove its users from selectedUserList
  const addGroupToSelected = (group: Group) => {
    const groupUsers: UserSearchType[] =
      (group.users ?? []).map((u) => ({
        ...(u as User),
        role: TeamMemberRole.Member,
      })) || [];

    // Add group UI entry (with its own users)
    setSelectedGroupList((prev) => {
      if (prev.some((g) => g.id === group.id)) return prev;

      const newGroup: SelectedGroupType = {
        ...group,
        users: groupUsers,
      } as SelectedGroupType;

      return [...prev, newGroup];
    });

    // Remove these users from selectedUserList (group "owns" them now)
    const groupUserIdsSet = new Set(groupUsers.map((u) => u.id));
    setSelectedUserList((prevUsers) =>
      prevUsers.filter((u) => !groupUserIdsSet.has(u.id))
    );

    // Ensure this group is expanded by default in the assign panel
    if (!expandedGroupIds.includes(group.id)) {
      toggleGroupExpanded(group.id);
    }
  };

  // Add a single user into selectedUserList (deduplicated, default role Member)
  const addUserToSelected = (user: User) => {
    setSelectedUserList((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev;

      const userWithRole: UserSearchType = {
        ...(user as User),
        role: TeamMemberRole.Member,
      };

      return [...prev, userWithRole];
    });
  };

  const onMembersAddedRef = useRef(onMembersAdded);
  onMembersAddedRef.current = onMembersAdded;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [
    createTeamMemberInvitation,
    {
      loading: isUpdatingMembers,
      data: createTeamMemberInvitationData,
      error: createTeamMemberInvitationError,
    },
  ] = useCreateTeamMemberInvitationMutation({
    refetchQueries: team?.uuid
      ? [
          {
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
          },
          ...(team?.id
            ? [
                {
                  query: ListPendingUserRequestsDocument,
                  variables: {
                    attributes: {
                      requestType: UserRequestCategory.MemberAddition,
                      userRequestableId: team.id,
                      userRequestableType: UserRequestable.Account,
                    },
                  },
                },
              ]
            : []),
        ]
      : [],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!createTeamMemberInvitationData) return;
    const complete = async () => {
      logEvent({ name: 'invite_sent' });
      setSelectedUserList([]);
      setSelectedEmailList([]);
      setSelectedGroupList([]);
      setText('');
      onMembersAddedRef.current?.();
      onCloseRef.current();

      if (team?.uuid && client) {
        try {
          const { data } = await client.query({
            query: GetTeamDetailDocument,
            variables: { id: team.uuid, includePending: true },
            fetchPolicy: 'network-only',
          });
          // getTeamDetail resolves getPublicTeam, a TeamOrPublicTeam union.
          // Only the Team branch carries the member fields selectedTeam needs.
          if (data?.getPublicTeam?.__typename === 'Team') {
            setSelectedTeam(data.getPublicTeam as Team);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating team context:', error);
        }
      }

      toast.show(
        <StatusToast
          message='Members invited successfully'
          duration={3000}
          type='success'
        />,
        {
          type: 'custom',
          duration: 3000,
          placement: 'bottom',
          animationType: 'slide-in',
          style: {
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
        }
      );
    };
    complete();
  }, [createTeamMemberInvitationData]);

  useEffect(() => {
    if (!createTeamMemberInvitationError) return;
    toast.show(
      <StatusToast
        message='Invitation failed. Try again'
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
        },
      }
    );
  }, [createTeamMemberInvitationError]);

  const onCopyLinkPressed = useCallback(async () => {
    if (!teamId || copyLinkLoading) return;
    try {
      await createTeamInvitation({ variables: { id: teamId } });
    } catch {
      // onCompleted won't fire; clipboard is left unchanged
    }
  }, [teamId, copyLinkLoading, createTeamInvitation]);

  // Render UI for User / StringSearchType / Tasktag "More on TaskTag" item
  const renderUser = (item: any, isSuggestion: boolean = false) => {
    if (item.__typename === 'str') {
      return (
        <Text
          variant='webBodySecondary'
          color='textPrimary'
          flexShrink={1}
          ellipsizeMode='tail'
          numberOfLines={1}>
          More on TaskTag
        </Text>
      );
    }
    if (item.__typename === 'StringSearchType') {
      return (
        <Box key={item.title} flex={1} flexDirection='row' alignItems='center'>
          <Box
            width={40}
            height={40}
            borderRadius='l'
            backgroundColor='grey02'
            alignItems='center'
            justifyContent='center'>
            <Icon color='grey04' variant='m' name='Mail' />
          </Box>
          <Box ml='xs'>
            <Text
              variant='webBodySecondary'
              color='textPrimary'
              flexShrink={1}
              ellipsizeMode='tail'
              numberOfLines={1}>
              {isSuggestion ? 'Invite: ' + item.title : item.title}
            </Text>
          </Box>
        </Box>
      );
    }

    const displayName = getDisplayName(item as User);
    const { avatar, email, id } = item as User;

    const AvatarComponent = Avatar as any;
    return (
      <Box key={id} flex={1} flexDirection='row' alignItems='center'>
        <AvatarComponent
          disableAvatarPress={true}
          size='medium'
          id={item.id}
          avatar={avatar}
          label={displayName}
          isMultiple={false}
        />
        <Box ml='xs'>
          <Text
            variant='webBodySecondary'
            color='textPrimary'
            flexShrink={1}
            ellipsizeMode='tail'
            numberOfLines={1}>
            <Highlighter
              autoEscape
              highlightStyle={{
                backgroundColor: theme.colors.yellowBright,
              }}
              searchWords={[searchVal]}
              textToHighlight={displayName}
            />
          </Text>
          {!!email && (
            <Text
              flexShrink={1}
              variant='webBodyHintSubline'
              color='grey04'
              marginTop='xxs'
              ellipsizeMode='tail'
              numberOfLines={1}>
              <Highlighter
                autoEscape
                highlightStyle={{
                  backgroundColor: theme.colors.yellowBright,
                }}
                searchWords={[searchVal]}
                textToHighlight={email}
              />
            </Text>
          )}
        </Box>
      </Box>
    );
  };

  const renderItem = ({ item }: ListRenderItemInfo<any>) => {
    const id = item.__typename === 'StringSearchType' ? '-1' : item.id;
    const isTasktagUserFirst = item.__typename === 'str';

    // Special handling for Group in search dropdown:
    // - Chevron click: expand / collapse (with animation)
    // - Text area click: select the whole group
    if (item.__typename === 'Group') {
      const group = item as Group;
      // Default is expanded when not in collapsed list
      const isExpanded = !searchExpandedGroupIds.includes(group.id);

      return (
        <SimpleHoverable>
          {(hover) => (
            <Box>
              {/* Group header row in dropdown */}
              <Box
                flex={1}
                px='m'
                py='s'
                borderBottomColor='grey02'
                borderBottomWidth={isTasktagUserFirst ? 0 : 1}
                key={id}
                style={{ backgroundColor: hover ? '#FAFBFC' : 'white' }}
                justifyContent='center'>
                <Box flex={1} flexDirection='row' alignItems='center'>
                  {/* Chevron: only expand / collapse, do NOT select group */}
                  <TouchableOpacity
                    onPress={() => {
                      toggleSearchGroupExpanded(group.id);
                    }}>
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      variant='l'
                      marginHorizontal='xs'
                      color='grey05'
                    />
                  </TouchableOpacity>

                  {/* Name + count: click = select group */}
                  <TouchableOpacity
                    style={{ flex: 1 }}
                    onPress={() => {
                      addGroupToSelected(group);
                      setText('');
                      setFocusOnSearch(false);
                    }}>
                    <Box ml='xs'>
                      <Text
                        variant='webBodySecondary'
                        color='textPrimary'
                        flexShrink={1}
                        ellipsizeMode='tail'
                        numberOfLines={1}>
                        <Highlighter
                          autoEscape
                          highlightStyle={{
                            backgroundColor: theme.colors.yellowBright,
                          }}
                          searchWords={[searchVal]}
                          textToHighlight={group.name}
                        />
                      </Text>
                      <Text
                        flexShrink={1}
                        variant='webBodyHintSubline'
                        color='grey04'
                        marginTop='xxs'
                        ellipsizeMode='tail'
                        numberOfLines={1}>
                        {`${(group.users ?? []).length} members`}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                </Box>
              </Box>

              {/* Group users in dropdown (click = select single user), collapsible */}
              {isExpanded &&
                (group.users ?? []).map((gu: User) => {
                  const isUserInTeam = isUserAlreadyInTeam(gu.id);
                  return (
                    <SimpleHoverable
                      key={`${group.id}:${gu.id}`}
                      {...({
                        onPress: () => {
                          if (!isUserInTeam) {
                            addUserToSelected(gu);
                            setText('');
                            setFocusOnSearch(false);
                          }
                        },
                      } as any)}>
                      {(hover1) => (
                        <Box
                          flex={1}
                          pr='m'
                          py='s'
                          borderBottomColor='grey02'
                          borderBottomWidth={isTasktagUserFirst ? 0 : 1}
                          style={{
                            backgroundColor: hover1 ? '#FAFBFC' : 'white',
                            paddingLeft: 64, // Indent group members
                          }}
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='space-between'>
                          <Box flex={1}>{renderUser(gu, true)}</Box>
                          {isUserInTeam && (
                            <Text variant='labelRequired' color='grey04'>
                              Already added
                            </Text>
                          )}
                        </Box>
                      )}
                    </SimpleHoverable>
                  );
                })}
            </Box>
          )}
        </SimpleHoverable>
      );
    }

    // Normal items in dropdown (contacts / tasktag users / StringSearchType / "More on TaskTag")
    const isUserItem = item.__typename === 'User';
    const isUserInTeam = isUserItem && isUserAlreadyInTeam((item as User).id);

    return (
      <SimpleHoverable
        {...({
          onPress: () => {
            // Temporarily disabled invite by email
            if (item.__typename === 'StringSearchType') {
              // Add email suggestion to selectedEmailList
              setSelectedEmailList((prev) => {
                const emailItem = item as StringSearchType;
                if (prev.some((e) => e.title === emailItem.title)) return prev;
                return [...prev, emailItem];
              });
            } else if (item.__typename === 'User' && !isUserInTeam) {
              addUserToSelected(item as User);
            } else if (item.__typename === 'str') {
              // "More on TaskTag" entry, handle as needed
            }

            if (!isUserInTeam) {
              setText('');
              setFocusOnSearch(false);
            }
          },
        } as any)}>
        {(hover) => (
          <Box
            flex={1}
            px='m'
            py='s'
            borderBottomColor='grey02'
            borderBottomWidth={isTasktagUserFirst ? 0 : 1}
            key={id}
            style={{ backgroundColor: hover ? '#FAFBFC' : 'white' }}
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'>
            <Box flex={1}>{renderUser(item, true)}</Box>
            {isUserInTeam && (
              <Text variant='labelRequired' color='grey04'>
                Already added
              </Text>
            )}
          </Box>
        )}
      </SimpleHoverable>
    );
  };

  // Render item in the assign panel (bottom list)
  const renderItemAssign = ({ item }: ListRenderItemInfo<any>) => {
    const isString = item.__typename === 'StringSearchType';
    const isGroup = item.__typename === 'Group';
    const id = isString ? item.title : item.id.toString();

    // Group row in assign panel: group header + nested users
    if (isGroup) {
      const group = item as SelectedGroupType;
      const groupUsers = group.users ?? [];
      const isExpanded = expandedGroupIds.includes(group.id);
      return (
        <Box key={id}>
          {/* Group header */}
          <Box
            flex={1}
            px='xs'
            py='s'
            borderBottomColor='grey02'
            borderBottomWidth={1}
            flexDirection='row'
            alignItems='center'>
            <Box flex={1} flexDirection='row' alignItems='center'>
              <TouchableOpacity onPress={() => toggleGroupExpanded(group.id)}>
                <Icon
                  name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                  variant='l'
                  marginHorizontal='xs'
                  color='grey05'
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1 }}
                onPress={() => toggleGroupExpanded(group.id)}>
                <Box ml='xs'>
                  <Text
                    variant='webBodySecondary'
                    color='textPrimary'
                    flexShrink={1}
                    ellipsizeMode='tail'
                    numberOfLines={1}>
                    {group.name}
                  </Text>
                  <Text
                    flexShrink={1}
                    variant='webBodyHintSubline'
                    color='grey04'
                    marginTop='xxs'
                    ellipsizeMode='tail'
                    numberOfLines={1}>
                    {`${groupUsers.length} members`}
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>

            {/* DELETE GROUP: remove group and all its users */}
            <SimpleHoverable
              {...({
                onPress: () => {
                  LayoutAnimation.configureNext(
                    LayoutAnimation.Presets.easeInEaseOut
                  );

                  // Remove the whole group from selectedGroupList
                  setSelectedGroupList((prev) =>
                    prev.filter((g) => g.id !== group.id)
                  );

                  // Collapse group if expanded before removing
                  if (expandedGroupIds.includes(group.id)) {
                    toggleGroupExpanded(group.id);
                  }

                  // Remove all group users from selectedUserList
                  const groupUserIdsSet = new Set(groupUsers.map((u) => u.id));
                  setSelectedUserList((prev) =>
                    prev.filter((u) => !groupUserIdsSet.has(u.id))
                  );
                },
              } as any)}>
              {(hover) => (
                <Box
                  width={32}
                  height={32}
                  borderRadius='xs'
                  backgroundColor={hover ? 'grey01' : 'white'}
                  alignItems='center'
                  justifyContent='center'>
                  <Icon
                    color={hover ? 'alertRed' : 'grey05'}
                    variant='s'
                    name='Trash2'
                  />
                </Box>
              )}
            </SimpleHoverable>
          </Box>

          {/* Group users under the group header (indented, collapsible) */}
          {isExpanded &&
            groupUsers.map((member) => (
              <SimpleHoverable
                key={member.id}
                {...({ onPress: () => {} } as any)}>
                {(hoverMember) => (
                  <Box
                    flex={1}
                    px='xs'
                    py='s'
                    borderBottomColor='grey02'
                    borderBottomWidth={1}
                    flexDirection='row'
                    backgroundColor={hoverMember ? 'grey01' : 'white'}
                    alignItems='center'
                    style={{ paddingLeft: 40 }}>
                    <Box flex={1}>{renderUser(member, false)}</Box>

                    {/* Role selector dropdown */}
                    <BaseButtonV3
                      noBorder={true}
                      noRadius={false}
                      noHover={false}
                      buttonRadius='xs'
                      mr='xs'
                      modalRadius={8}
                      showHoverWhenDropdown={true}
                      popModalComponent={({ onClose }) => {
                        return (
                          <TeamMemberRoleSelector
                            onRoleSelect={(role) => {
                              // Update role in group users
                              setSelectedGroupList((prev) =>
                                prev.map((g) => {
                                  if (g.id !== group.id) return g;
                                  const updatedUsers =
                                    g.users?.map((u) =>
                                      u.id === member.id ? { ...u, role } : u
                                    ) ?? [];
                                  return {
                                    ...g,
                                    users: updatedUsers,
                                  } as SelectedGroupType;
                                })
                              );
                              // Update role in global selectedUserList
                              setSelectedUserList((prev) =>
                                prev.map((u) =>
                                  u.id === member.id ? { ...u, role } : u
                                )
                              );
                              onClose();
                            }}
                          />
                        );
                      }}>
                      <Box
                        width={86}
                        mx='m'
                        alignItems='center'
                        justifyContent='flex-end'
                        flexDirection='row'>
                        <Text
                          variant='webSmall'
                          color='textPrimary'
                          marginRight='xs'>
                          {(member as unknown as UserSearchType).role ===
                          TeamMemberRole.Admin
                            ? 'Admin'
                            : 'Member'}
                        </Text>
                        <Icon color='grey05' variant='s' name='ChevronDown' />
                      </Box>
                    </BaseButtonV3>

                    {/* Delete this member from the group & from selectedUserList */}
                    <SimpleHoverable
                      {...({
                        onPress: () => {
                          LayoutAnimation.configureNext(
                            LayoutAnimation.Presets.easeInEaseOut
                          );

                          // Remove from group users
                          setSelectedGroupList((prev) =>
                            prev.map((g) => {
                              if (g.id !== group.id) return g;
                              const updatedUsers =
                                g.users?.filter((u) => u.id !== member.id) ??
                                [];
                              return {
                                ...g,
                                users: updatedUsers,
                              } as SelectedGroupType;
                            })
                          );

                          // Remove from global selectedUserList
                          setSelectedUserList((prev) =>
                            prev.filter((u) => u.id !== member.id)
                          );
                        },
                      } as any)}>
                      {(hover) => (
                        <Box
                          width={32}
                          height={32}
                          borderRadius='xs'
                          backgroundColor={hover ? 'grey01' : 'white'}
                          alignItems='center'
                          justifyContent='center'>
                          <Icon
                            color={hover ? 'alertRed' : 'grey05'}
                            variant='s'
                            name='Trash2'
                          />
                        </Box>
                      )}
                    </SimpleHoverable>
                  </Box>
                )}
              </SimpleHoverable>
            ))}
        </Box>
      );
    }

    // Non-group items in assign panel: StringSearchType and normal User
    const isStringType = item.__typename === 'StringSearchType';

    return (
      <Box
        flex={1}
        px='xs'
        py='s'
        borderBottomColor='grey02'
        borderBottomWidth={1}
        flexDirection='row'
        key={id}
        alignItems='center'>
        {/* Left: render user / string item */}
        <Box flex={1}>{renderUser(item, false)}</Box>

        {/* Role selector dropdown */}
        <BaseButtonV3
          noBorder={true}
          noRadius={false}
          noHover={false}
          buttonRadius='xs'
          mr='xs'
          modalRadius={8}
          showHoverWhenDropdown={true}
          popModalComponent={({ onClose }) => {
            return (
              <TeamMemberRoleSelector
                onRoleSelect={(role) => {
                  if (isStringType) {
                    setSelectedEmailList((prev) =>
                      prev.map((e) =>
                        e.title === (item as StringSearchType).title
                          ? { ...e, role }
                          : e
                      )
                    );
                  } else {
                    setSelectedUserList((prev) =>
                      prev.map((u) => (u.id === item.id ? { ...u, role } : u))
                    );
                  }
                  onClose();
                }}
              />
            );
          }}>
          <Box
            width={86}
            mx='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='textPrimary' marginRight='xs'>
              {(isStringType
                ? (item as StringSearchType).role
                : (item as UserSearchType).role) === TeamMemberRole.Admin
                ? 'Admin'
                : 'Member'}
            </Text>
            <Icon color='grey05' variant='s' name='ChevronDown' />
          </Box>
        </BaseButtonV3>

        {/* Delete button */}
        <SimpleHoverable
          {...({
            onPress: () => {
              if (isStringType) {
                setSelectedEmailList((prev) =>
                  prev.filter(
                    (t) => t.title !== (item as StringSearchType).title
                  )
                );
              } else {
                setSelectedUserList((prev) =>
                  prev.filter((t) => t.id !== item.id)
                );
              }
            },
          } as any)}>
          {(hover) => (
            <Box
              width={32}
              height={32}
              borderRadius='xs'
              backgroundColor={hover ? 'grey01' : 'white'}
              alignItems='center'
              justifyContent='center'>
              <Icon
                color={hover ? 'alertRed' : 'grey05'}
                variant='s'
                name='Trash2'
              />
            </Box>
          )}
        </SimpleHoverable>
      </Box>
    );
  };

  return (
    <WebModalV3
      canOverflowModal={true}
      accessibilityLabel='Assign Task Member'
      onClose={() => {
        handleCancel();
      }}
      visible={isVisible}
      width={800}
      height={412}>
      <Box
        py='xs'
        paddingHorizontal='m'
        justifyContent='flex-start'
        width={800}
        // Make the whole modal body a positioning context
        position='relative'>
        {copied && (
          <Box
            position='absolute'
            top={48}
            right={56}
            zIndex={9999}
            pointerEvents='none'>
            <Box
              backgroundColor='white'
              borderWidth={1}
              borderColor='grey02'
              borderRadius='xxs'
              minWidth={129}
              flexDirection='row'
              justifyContent='center'
              alignItems='center'
              py='xs'
              px='s'
              style={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}>
              <Icon name='CheckCircle' color='greenSecondary' variant='s' />
              <Text
                ml='xs'
                accessibilityLabel='Copy link'
                variant='labelSmall'
                color='black'>
                Link copied!
              </Text>
            </Box>
          </Box>
        )}
        {/* Header with title, copy link and close icon */}
        <Box flexDirection='row' mt='m' alignItems='center'>
          <Text flex={1} variant='labelLarge' color='textPrimary'>
            Invite or Add Member
          </Text>

          {teamId !== '' && (
            <SimpleHoverable
              onPress={onCopyLinkPressed}
              disabled={copyLinkLoading}>
              {(isHovered) => (
                <Box
                  width={122}
                  backgroundColor={
                    copyLinkLoading || isHovered ? 'grey01' : 'white'
                  }
                  flexDirection='row'
                  justifyContent='center'
                  alignItems='center'
                  borderRadius='xs'
                  paddingVertical='xs'
                  paddingHorizontal='s'>
                  <Icon color='blue' variant='s' name='Link' marginRight='xs' />
                  <Text flex={1} variant='labelSmall' color='blue'>
                    {copyLinkLoading ? 'Getting link…' : 'Copy Link'}
                  </Text>
                </Box>
              )}
            </SimpleHoverable>
          )}

          <WebIcon
            accessibilityLabel='Close'
            name='X'
            variant='l'
            color='grey05'
            onPress={handleCancel}
          />
        </Box>

        {/* Search input */}
        <Box mt='s'>
          <AddMembersInput
            focusOnSearch={focusOnSearch}
            setFocusOnSearch={setFocusOnSearch}
            searchVal={searchVal}
            setText={setText}
            inputRef={inputRef}
            placeholder='Add members by email, name or group'
          />
        </Box>

        {/* Dropdown for search results */}
        {shouldShowDropdown &&
          (() => {
            // Order: email suggestions, contacts, groups, TaskTag users
            const allItems = [
              ...suggestionList,
              ...filteredList,
              ...listGroup,
              ...(filteredTasktagUsers.length > 0
                ? [
                    {
                      name: 'More on TaskTag',
                      __typename: 'str',
                    },
                  ]
                : []),
              ...filteredTasktagUsers,
            ];

            // Calculate total visible items including expanded group members
            // Groups are expanded by default (isExpanded = !searchExpandedGroupIds.includes(group.id))
            let totalVisibleItems = allItems.length;
            listGroup.forEach((group) => {
              const isExpanded = !searchExpandedGroupIds.includes(group.id);
              if (isExpanded) {
                // Count each expanded group member as a separate visible item
                totalVisibleItems += group.users?.length || 0;
              }
            });

            // Calculate if scrollbar should appear (items > 7)
            // Email suggestion counts as item 1, so scrollbar appears when total > 7
            const shouldShowScrollbar = totalVisibleItems > 7;
            // Each item is approximately 56px tall, so 7 items = ~392px
            const maxHeight = shouldShowScrollbar ? 456 : undefined;

            const dropdownStyle = {
              position: 'absolute',
              // Position dropdown just below the input
              top: 64 + 16 + 48 + 4,
              left: 16,
              right: 16,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: '#E8E8E8',
              backgroundColor: '#FFFFFF',
              maxHeight: maxHeight || 'auto',
              minHeight: 180,
              zIndex: 10000,
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
            } as any;

            // TA-10280: 3 skeleton rows during the debounced search window,
            // or while the existing-contacts suggestion list is still
            // fetching on modal open (before any search term is typed) —
            // never render an empty-looking box while either is in flight.
            if (isSearchLoading || (contactsLoading && !searchVal.trim())) {
              return (
                <Box style={dropdownStyle}>
                  <SkeletonList
                    row={(index) => <MemberRowSkeleton key={index} />}
                  />
                </Box>
              );
            }

            return (
              <FlatList
                style={dropdownStyle}
                keyExtractor={(item: any) => {
                  if (item.__typename === 'StringSearchType')
                    return `email:${item.title}`;
                  if (item.__typename === 'str') return 'more-on-tasktag';
                  if (item.__typename === 'Group') return `group:${item.id}`;
                  return `user:${item.id}`;
                }}
                data={allItems}
                renderItem={renderItem}
                ListEmptyComponent={null}
                persistentScrollbar={true}
              />
            );
          })()}

        {/* Empty state */}
        {assignDisable && (
          <Box mt='s' alignItems='center'>
            <Image
              style={{ width: 86, height: 86 }}
              source={require('../../../../assets/images/teammates.png')}
            />
            <Text
              mt='xs'
              mb='xxxs'
              flex={1}
              textAlign='center'
              variant='webBodySecondary'
              color='textPrimary'>
              Bring your team on board
            </Text>
            <Text
              my='xs'
              flex={1}
              textAlign='center'
              variant='webBodyHintSubline'
              color='grey04'>
              Invite teammates or contact groups to start working together
              seamlessly.
            </Text>
          </Box>
        )}

        {/* Assign panel: groups + visible standalone users + emails */}
        {!assignDisable && (
          <Box minHeight={56} maxHeight={56 * 8}>
            <FlatList
              keyExtractor={(item: any) => {
                if (item.__typename === 'StringSearchType')
                  return `email:${item.title}`;
                if (item.__typename === 'str') return 'more-on-tasktag';
                if (item.__typename === 'Group') return `group:${item.id}`;
                return `user:${item.id}`;
              }}
              keyboardShouldPersistTaps='handled'
              data={[
                ...selectedEmailList,
                ...selectedGroupList,
                ...visibleSelectedUsers,
              ]}
              renderItem={renderItemAssign}
              ListEmptyComponent={() => <Box />}
              contentContainerStyle={{ paddingVertical: 4 }}
            />
          </Box>
        )}

        {/* Assign button */}
        <Button
          disabled={assignDisable}
          isLoading={isUpdatingMembers}
          btnTextColor={assignDisable ? 'grey04' : 'white'}
          onPress={async () => {
            // Merge standalone users and group users for the final payload
            const usersFromGroups: UserSearchType[] = selectedGroupList.flatMap(
              (g) => g.users ?? []
            );

            const merged: UserSearchType[] = [];
            const seen = new Set<string>();

            // Standalone users take precedence
            [...selectedUserList, ...usersFromGroups].forEach((u) => {
              if (!seen.has(u.id)) {
                seen.add(u.id);
                merged.push(u);
              }
            });

            // Prepare teamMembers array from users
            const teamMembersFromUsers = merged
              .map((u) => {
                const email = u.email?.trim();
                if (!email) {
                  // eslint-disable-next-line no-console
                  console.warn('Skipping user with no email:', u);
                  return null;
                }
                return {
                  email,
                  role: u.role || TeamMemberRole.Member,
                };
              })
              .filter(
                (member): member is { email: string; role: TeamMemberRole } =>
                  member !== null
              );

            // Prepare teamMembers array from selected emails
            const teamMembersFromEmails = selectedEmailList
              .map((emailItem) => {
                const email = emailItem.title?.trim();
                if (!email) {
                  // eslint-disable-next-line no-console
                  console.warn('Skipping email with no title:', emailItem);
                  return null;
                }
                return {
                  email,
                  role: emailItem.role || TeamMemberRole.Member,
                };
              })
              .filter(
                (member): member is { email: string; role: TeamMemberRole } =>
                  member !== null
              );

            // Combine both arrays
            const teamMembers = [
              ...teamMembersFromUsers,
              ...teamMembersFromEmails,
            ];

            // Validate teamMembers array is not empty
            if (teamMembers.length === 0) {
              toast.show(
                <StatusToast
                  message='No valid members to invite'
                  duration={3000}
                  type='error'
                />,
                {
                  type: 'custom',
                  duration: 3000,
                  placement: 'bottom',
                  animationType: 'slide-in',
                  style: {
                    backgroundColor: 'transparent',
                    shadowColor: 'transparent',
                  },
                }
              );
              return;
            }

            if (onDeferredSend) {
              setSelectedUserList([]);
              setSelectedEmailList([]);
              setSelectedGroupList([]);
              setText('');
              onDeferredSend(teamMembers);
              onClose();
              return;
            }

            const teamIdString = String(teamId);

            createTeamMemberInvitation({
              variables: {
                id: teamIdString,
                teamMembers,
              },
            });
          }}
          height={48}
          fullWidth
          backgroundColor={assignDisable ? 'grey02' : 'grey05'}
          marginTop='m'
          marginBottom='xxxs'
          accessibilityLabel='Asssign'>
          <Text variant='labelSmall' color={assignDisable ? 'grey04' : 'white'}>
            Send Invite
          </Text>
        </Button>

        {!requireMemberSelection && (
          <Button
            variant='text'
            // Deferred-creation flows have no teamId yet — skipping must
            // still work, so don't gate on teamId here
            onPress={() => {
              setSelectedUserList([]);
              setSelectedEmailList([]);
              setSelectedGroupList([]);
              setText('');
              onDeferredSend?.([]);
              // Some call sites pass the same handler for both props —
              // don't invoke it twice
              if (onMembersAdded && onMembersAdded !== onClose) {
                onMembersAdded([]);
              }
              onClose();
            }}>
            <Text variant='labelSmall' color='textSecondary'>
              Skip Now
            </Text>
          </Button>
        )}

        {/* Footer note */}
        <Box
          mt='xs'
          height={16}
          mb='m'
          flexDirection='row'
          justifyContent='center'
          alignItems='center'>
          <Icon color='grey04' variant='s' name='Pending' />
          <Text
            ml='xxs'
            textAlign='center'
            variant='webBodyHintSubline'
            color='grey04'>
            Invitations expire after 7 days
          </Text>
        </Box>
      </Box>
    </WebModalV3>
  );
};
